"use client"

import { Button, IconButton, Text } from "@/elements"
import { InviteToAJobDialog } from "@/components/InviteToJobDialog"
import { FragmentType, graphql, useFragment } from "@/gql"
import { ConnectOrMessageButton } from "@/components/ConnectOrMessageButton"
import type { NovaSession } from "@/types"
import { PencilIcon, Square2StackIcon } from "@heroicons/react/24/outline"
import { copyToClipboard } from "@/utils/copyToClipboard"
import { PortfolioUIState } from "../page"
import { PostAProjectButton } from "./PostAProjectButton"
import { ActionsModal } from "./ActionsModal"
import { CreativePublicVisibility, UserConnectionStatus } from "@/gql/graphql"
import { PortfolioEditModal, useEditPortfolio } from "./edit/useEditPortfolio"
import clsx from "clsx"

const HeaderActionButtonsFragment = graphql(`
  fragment HeaderActionButtons on IndividualCreative {
    id
    handle
    publicVisibility
    user {
      id
      uuid
      viewerConnectionStatus
      ...ConnectOrMessageButton
      ...ActionsModal
    }

    ...InviteToAJobDialog
  }
`)

interface Props {
  creative: FragmentType<typeof HeaderActionButtonsFragment> | undefined | null
  serverSession: NovaSession | null
  uiState: PortfolioUIState
  editProfileMethods?: ReturnType<typeof useEditPortfolio>
}

export const HeaderActionButtons: React.FC<Props> = props => {
  const { serverSession, uiState, editProfileMethods } = props
  const creative = useFragment(HeaderActionButtonsFragment, props.creative)
  const user = creative?.user
  const viewerConnectionStatus = user?.viewerConnectionStatus
  const publicVisibility = creative?.publicVisibility

  return (
    <div className="flex h-full flex-row items-center gap-3">
      <IconButton
        variant="white"
        className="hidden sm:flex"
        Icon={Square2StackIcon}
        onClick={() =>
          copyToClipboard(window.location.href, {
            successMessage: "Link copied to clipboard",
          })
        }
      />
      {uiState?.isUsersPortfolio && !editProfileMethods?.isEditing && (
        <IconButton
          variant="white"
          className="hidden sm:flex"
          Icon={PencilIcon}
          onClick={() => editProfileMethods?.toggleEdit(true)}
        />
      )}
      {editProfileMethods?.isEditing && (
        <div
          onClick={() => editProfileMethods?.setOpenModal(PortfolioEditModal.ProfileVisibility)}
          className="flex h-[25px] flex-shrink-0 cursor-pointer flex-row items-center rounded-sm bg-bg-2 p-1 hover:bg-bg-3">
          <Text
            variant="label"
            className={clsx(publicVisibility === CreativePublicVisibility.Private ? "text-red-dark" : "text-fg-2")}>
            <Text variant="label" element="span" className="text-fg-2">
              Visibility:{" "}
            </Text>
            {publicVisibility === CreativePublicVisibility.Private ? "Private" : "Public"}
          </Text>
        </div>
      )}
      {uiState?.isUsersPortfolio && !editProfileMethods?.isEditing && (
        <PostAProjectButton viewerCreativeHandle={creative?.handle!} entryPoint="portfolio_header" />
      )}
      {serverSession && !uiState?.isUsersPortfolio && (
        <ConnectOrMessageButton
          user={creative?.user}
          serverSession={serverSession}
          layout="row"
          trackingEntryPoint="portfolio-main-header"
        />
      )}
      {!uiState?.isUsersPortfolio && viewerConnectionStatus !== UserConnectionStatus.ConnectionRequestReceived && (
        <div className="hidden sm:flex">
          <InviteToAJobDialog
            trackingEntryPoint="portfolio-main-header"
            creative={creative}
            trigger={<Button variant="grey-outline">Invite to Job</Button>}
          />
        </div>
      )}
      {!uiState?.isUsersPortfolio && <ActionsModal user={creative?.user} isCreative serverSession={serverSession} />}
    </div>
  )
}
